import { collection, addDoc, query, where, orderBy, getDocs, deleteDoc, serverTimestamp, limit, startAfter } from 'firebase/firestore'
import { db } from '../config/firebase'
import FirestoreMonitor from './FirestoreMonitor'

export type LoginEntry = {
  id?: string
  userId: string
  timestamp: any // Firebase Timestamp
  userAgent?: string
  ipAddress?: string
  ttl?: Date // TTL dla automatycznego usuwania przez Firestore
}

class LoginHistoryService {
  private col = 'login_history'
  private pendingLogins = new Set<string>() // Track users with pending login records

  // Track cleanup timeouts to prevent memory leaks
  private cleanupTimeouts = new Map<string, NodeJS.Timeout>()
  
  // Cache dla historii logowań
  private cache = new Map<string, { entries: LoginEntry[], timestamp: number }>()
  private readonly CACHE_TTL = 60000 // 1 minuta

  async recordLogin(userId: string): Promise<void> {
    // Prevent multiple simultaneous calls for the same user
    if (this.pendingLogins.has(userId)) {
      return
    }

    this.pendingLogins.add(userId)

    try {
      // Bezpośrednie dodanie logowania - duplikaty obsługiwane przez debouncing w froncie
      const ttlDate = new Date()
      ttlDate.setDate(ttlDate.getDate() + 60) // 60 dni od teraz
      
      const loginData: Omit<LoginEntry, 'id'> = {
        userId,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        ttl: ttlDate // Firestore automatycznie usuwa po tej dacie
      }
      
      await addDoc(collection(db, this.col), loginData)
      FirestoreMonitor.recordWrite()
      
      // Invalidate cache for this user
      this.cache.delete(userId)
      
      // Clear any existing timeout for this user
      const existingTimeout = this.cleanupTimeouts.get(userId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }
      
      // Keep the user in pending set for additional 2 seconds to prevent rapid duplicates
      const timeout = setTimeout(() => {
        this.pendingLogins.delete(userId)
        this.cleanupTimeouts.delete(userId)
      }, 2000)
      
      this.cleanupTimeouts.set(userId, timeout)
      
    } catch (error) {
      console.error('Failed to record login:', error)
      this.pendingLogins.delete(userId) // Remove from pending on error
      
      // Clean up timeout on error
      const timeout = this.cleanupTimeouts.get(userId)
      if (timeout) {
        clearTimeout(timeout)
        this.cleanupTimeouts.delete(userId)
      }
    }
  }
  
  // Method to clean up all pending operations (call on app unmount)
  cleanup(): void {
    this.pendingLogins.clear()
    this.cache.clear()
    
    // Clear all timeouts
    this.cleanupTimeouts.forEach(timeout => clearTimeout(timeout))
    this.cleanupTimeouts.clear()
  }

  async getLoginHistoryPage(userId: string, limitCount: number = 5, startAfterDoc?: any): Promise<{ entries: LoginEntry[], hasMore: boolean, lastDoc?: any }> {
    try {
      // Sprawdź cache (tylko dla pierwszej strony bez startAfterDoc)
      if (!startAfterDoc) {
        const cached = this.cache.get(userId)
        if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
          const entries = cached.entries.slice(0, limitCount)
          const hasMore = cached.entries.length > limitCount
          return { entries, hasMore, lastDoc: undefined }
        }
      }
      
      // Zapytanie z filtrowaniem po userId (wymaga tylko podstawowego indeksu)
      // Pobieramy limitCount + 1 żeby sprawdzić czy są kolejne strony
      let q = query(
        collection(db, this.col),
        where('userId', '==', userId),
        limit(limitCount + 1)
      )
      
      if (startAfterDoc) {
        q = query(
          collection(db, this.col),
          where('userId', '==', userId),
          startAfter(startAfterDoc),
          limit(limitCount + 1)
        )
      }
      
      const snapshot = await getDocs(q)
      FirestoreMonitor.recordReads(snapshot.size)
      
      // Konwertuj dokumenty na LoginEntry
      const allEntries: LoginEntry[] = []
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        allEntries.push({
          id: doc.id,
          ...data
        } as LoginEntry)
      })
      
      // Sortuj po timestamp DESC (po stronie klienta)
      allEntries.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0
        const timeB = b.timestamp?.seconds || 0
        return timeB - timeA
      })
      
      // Sprawdź czy są kolejne strony
      const hasMore = allEntries.length > limitCount
      const entries = allEntries.slice(0, limitCount)
      
      // Zapisz do cache tylko pierwszą stronę
      if (!startAfterDoc) {
        this.cache.set(userId, {
          entries: entries,
          timestamp: Date.now()
        })
      }
      
      const lastDoc = entries.length > 0 ? snapshot.docs[entries.length - 1] : undefined
      
      return { entries, hasMore, lastDoc }
    } catch (error) {
      console.error('Failed to get login history page:', error)
      throw error
    }
  }

  // Zachowujemy starą metodę dla kompatybilności
  async getLoginHistory(userId: string, limitCount: number = 50): Promise<LoginEntry[]> {
    try {
      console.log('🔍 Collection name:', this.col)
      console.log('🔍 Database instance:', db)
      
      // Zoptymalizowane zapytanie z limit i orderBy
      const optimizedQ = query(
        collection(db, this.col),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount) // Używaj limitCount parametru zamiast pobierania wszystkich
      )
      
      console.log('🔍 Executing query...')
      const snapshot = await getDocs(optimizedQ)
      console.log('📊 Query executed successfully! Returned', snapshot.size, 'documents')
      
      const history: LoginEntry[] = []
      
      snapshot.forEach(doc => {
        const data = doc.data()
        console.log('📄 Login entry document ID:', doc.id)
        console.log('📄 Login entry data:', data)
        history.push({
          id: doc.id,
          ...data
        } as LoginEntry)
      })
      
      // Dane już posortowane przez Firestore orderBy
      console.log('✅ Final history array length:', history.length)
      console.log('✅ Final history array contents:', history)
      return history
    } catch (error) {
      console.error('❌ Failed to get login history for user:', userId)
      console.error('❌ Error details:', error)
      console.error('❌ Error message:', error instanceof Error ? error.message : 'Unknown error')
      console.error('❌ Error stack:', error instanceof Error ? error.stack : 'No stack trace')
      return []
    }
  }

  async getAllUsersLastLogin(): Promise<{ [userId: string]: LoginEntry }> {
    try {
      const q = query(
        collection(db, this.col),
        orderBy('timestamp', 'desc')
      )
      
      const snapshot = await getDocs(q)
      const lastLogins: { [userId: string]: LoginEntry } = {}
      
      snapshot.forEach(doc => {
        const entry = {
          id: doc.id,
          ...doc.data()
        } as LoginEntry
        
        // Only keep the most recent login for each user
        if (!lastLogins[entry.userId]) {
          lastLogins[entry.userId] = entry
        }
      })
      
      return lastLogins
    } catch (error) {
      console.error('❌ Failed to get all users last login:', error)
      return {}
    }
  }

  async deleteUserLoginHistory(userId: string): Promise<void> {
    try {
      console.log('🗑️ Deleting all login history for user:', userId)
      
      const q = query(
        collection(db, this.col),
        where('userId', '==', userId)
      )
      
      const snapshot = await getDocs(q)
      console.log(`📊 Found ${snapshot.size} login entries to delete for user:`, userId)
      
      // Delete all documents for this user
      const deletePromises = snapshot.docs.map(doc => {
        console.log('🗑️ Deleting login entry:', doc.id)
        return deleteDoc(doc.ref)
      })
      
      await Promise.all(deletePromises)
      
      console.log('✅ Successfully deleted all login history for user:', userId)
    } catch (error) {
      console.error('❌ Failed to delete login history for user:', userId, error)
      throw error // Re-throw to handle in UI
    }
  }
}

export default new LoginHistoryService()